// @file Vuex Store for handling comment
import { SAVE_CHANGES_FAILED } from 'bits/confirmation_dialog'
import { captureFetchException } from 'bits/error_tracker'
import PadletApi from 'surface/padlet_api'
import { Cid, Comment, CommentId } from 'types'
import Vue from 'vue'
import { Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'

interface CommentState {
  commentsFetched: boolean
  commentEntities: { [id: number]: Comment }
  newCommentBodyByPostCid: { [postCid: string]: string }
  pendingCommentIds: CommentId[]
  commentBeingEditedId: CommentId | null
  actionMenuCommentId: CommentId | null
  addCommentInputFocusedForPostId: CommentId | null
}

const defaultState = (): CommentState => ({
  commentsFetched: false,
  commentEntities: {},
  newCommentBodyByPostCid: {},
  pendingCommentIds: [],
  commentBeingEditedId: null,
  actionMenuCommentId: null,
  addCommentInputFocusedForPostId: null,
})

const CommentModule: Module<CommentState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    commentIds: (state): string[] => {
      return Object.keys(state.commentEntities)
    },
    commentIdsByPost: (state, getters): { [wishId: number]: Comment[] } => {
      const commentsByPost = {}
      getters.commentIds.forEach(rid => {
        const r = state.commentEntities[rid]
        commentsByPost[r.wish_id] = commentsByPost[r.wish_id] || []
        commentsByPost[r.wish_id].push(rid)
      })
      return commentsByPost
    },
    isCommenting(state): boolean {
      return !!state.commentBeingEditedId || state.addCommentInputFocusedForPostId !== null
    },
    newCommentBodyForPostCid(state): (postCid: Cid) => string {
      return (postCid: Cid): string => state.newCommentBodyByPostCid[postCid]
    },
  },
  mutations: {
    FETCH_COMMENTS_SUCCESS(state, comments): void {
      if (comments) {
        const newCommentEntities = {}
        comments.forEach(r => {
          newCommentEntities[r.id] = r
        })
        state.commentEntities = newCommentEntities // Trigger state change once
        state.pendingCommentIds = []
      }
      state.commentsFetched = true
    },
    UPDATE_COMMENT(state, { id, obj }): void {
      if (id < 0 || !obj) {
        return
      }
      const updatedEntity = {
        ...state.commentEntities[id],
        ...obj,
      }
      state.commentEntities = {
        ...state.commentEntities,
        [id]: updatedEntity,
      }
    },
    ADD_PENDING_COMMENT(state, pendingComment): void {
      state.commentEntities = {
        ...state.commentEntities,
        [pendingComment.id]: pendingComment,
      }
      state.pendingCommentIds.push(pendingComment.id)
    },
    REMOVE_PENDING_COMMENT(state, { commentId }): void {
      const isCommentPending = state.pendingCommentIds.includes(commentId)
      if (!isCommentPending) return
      state.pendingCommentIds = state.pendingCommentIds.filter(id => id !== commentId)
      Vue.delete(state.commentEntities, commentId)
    },
    REVERT_PENDING_COMMENT(state, { commentId, postCid }): void {
      const isCommentPending = state.pendingCommentIds.includes(commentId)
      if (!isCommentPending) return
      state.pendingCommentIds = state.pendingCommentIds.filter(id => id !== commentId)
      const comment = state.commentEntities[commentId]
      Vue.delete(state.commentEntities, commentId)
      Vue.set(state.newCommentBodyByPostCid, postCid, comment.body)
    },
    ADD_COMMENT(state, { comment, tempId }): void {
      // Remove old comment if present
      if (state.commentEntities[tempId]) Vue.delete(state.commentEntities, tempId)
      if (state.pendingCommentIds.includes(tempId)) {
        state.pendingCommentIds = state.pendingCommentIds.filter(id => id !== tempId)
      }
      // Add updated comment
      state.commentEntities = { ...state.commentEntities, [comment.id]: comment }
    },
    REMOVE_COMMENT(state, id): void {
      const newCommentEntities = { ...state.commentEntities }
      delete newCommentEntities[id]
      state.commentEntities = newCommentEntities
    },
    EDIT_COMMENT_REMOTE(state, comment): void {
      const originalComment = state.commentEntities[comment.id]
      // real time can be out of sync sometimes.
      // here we make sure we are not updating a new copy with a stale one
      if (originalComment && originalComment.updated_at <= comment.updated_at) {
        state.commentEntities = {
          ...state.commentEntities,
          [comment.id]: comment,
        }
      }
    },
    START_EDITING_COMMENT(state, commentId): void {
      state.commentBeingEditedId = commentId
    },
    STOP_EDITING_COMMENT(state): void {
      state.commentBeingEditedId = null
    },
    SHOW_COMMENT_ACTION_MENU(state, commentId): void {
      if (state.actionMenuCommentId) {
        state.actionMenuCommentId = null
      } else {
        state.actionMenuCommentId = commentId
      }
    },
    HIDE_COMMENT_ACTION_MENU(state): void {
      state.actionMenuCommentId = null
    },
    ADD_COMMENT_INPUT_FOCUSED(state, postId): void {
      state.addCommentInputFocusedForPostId = postId
    },
    ADD_COMMENT_INPUT_BLURRED(state, postId): void {
      if (state.addCommentInputFocusedForPostId === postId) {
        state.addCommentInputFocusedForPostId = null
      }
    },
    UPDATE_NEW_COMMENT_BODY(state, { postCid, body }): void {
      Vue.set(state.newCommentBodyByPostCid, postCid, body)
    },
  },
  actions: {
    /* ------------
     * CRUD
     * ------------ */
    pingComments({ rootState }): void {
      // Only send the network request to update the cache, do nothing with the response
      PadletApi.Comment.readAll({ wall: { id: rootState.wall.id } }).catch(e => {
        captureFetchException(e, { source: 'fetchComments' })
      })
    },
    fetchComments({ dispatch, rootState }): void {
      PadletApi.Comment.readAll({ wall: { id: rootState.wall.id } })
        .then(fetchedData => {
          dispatch('fetchCommentsSuccess', { fetchedData })
        })
        .catch(e => {
          captureFetchException(e, { source: 'fetchComments' })
        })
    },
    fetchCommentsSuccess({ commit }, { fetchedData }): void {
      const comments = fetchedData.data.map(comment => comment.attributes)

      commit('FETCH_COMMENTS_SUCCESS', comments)
    },
    async createComment({ commit, rootState, dispatch, rootGetters }, { postId, body, createdAt }): Promise<void> {
      // tempId will be used to identify the comment in the frontend until it is persisted,
      // after which, we will use the server-provided Id.
      const tempId = 'new_' + new Date().valueOf()
      const newComment = {
        id: tempId,
        wish_id: postId,
        body: body,
        created_at: createdAt,
        user: rootState.user,
        user_id: rootState.user.id,
      }
      // not handling pending comment for now, request is quick enough
      commit('ADD_PENDING_COMMENT', newComment)

      try {
        const comment = (await PadletApi.Comment.create(newComment)).data.attributes
        commit('ADD_COMMENT', { comment, tempId })
      } catch {
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
        const post = rootGetters['post/getPostByServerId'](postId)
        if (!post || !post.cid) {
          commit('REMOVE_PENDING_COMMENT', { commentId: tempId })
        } else {
          commit('REVERT_PENDING_COMMENT', { commentId: tempId, postCid: post.cid })
        }
      }
    },
    deleteComment({ commit, state }, { commentId }): void {
      const existingComment = state.commentEntities[commentId]
      commit('STOP_EDITING_COMMENT')
      if (existingComment) {
        commit('REMOVE_COMMENT', existingComment.id)
        PadletApi.Comment.delete(existingComment)
      }
    },
    startEditingComment({ commit }, { commentId }): void {
      commit('START_EDITING_COMMENT', commentId)
    },
    stopEditingComment({ commit }): void {
      commit('STOP_EDITING_COMMENT')
    },
    async editComment({ commit, dispatch }, { id, body }): Promise<void> {
      try {
        commit('UPDATE_COMMENT', { id: id, obj: { body, html_body: '' } })
        commit('STOP_EDITING_COMMENT')
        const {
          data: {
            attributes: { html_body },
          },
        } = await PadletApi.Comment.update({ id, body })
        commit('UPDATE_COMMENT', { id: id, obj: { html_body } })
      } catch {
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
        dispatch('startEditingComment', { commentId: id })
      }
    },
    addCommentInputFocused({ commit }, { postId }): void {
      commit('ADD_COMMENT_INPUT_FOCUSED', postId)
    },
    addCommentInputBlurred({ commit }, { postId }): void {
      commit('ADD_COMMENT_INPUT_BLURRED', postId)
    },
    updateNewCommentBody({ commit }, { postCid, body }): void {
      commit('UPDATE_NEW_COMMENT_BODY', { postCid, body })
    },

    /* ------------
     * REMOTE
     * ------------ */
    newCommentRemote({ commit }, comment): void {
      commit('ADD_COMMENT', { comment })
    },
    editCommentRemote({ commit }, payload): void {
      commit('EDIT_COMMENT_REMOTE', payload)
    },
    deleteCommentRemote({ commit, state }, comment): void {
      if (state.commentBeingEditedId && state.commentBeingEditedId === comment.id) {
        commit('STOP_EDITING_COMMENT')
      }
      if (state.actionMenuCommentId && state.actionMenuCommentId === comment.id) {
        commit('HIDE_COMMENT_ACTION_MENU')
      }
      commit('REMOVE_COMMENT', comment.id)
    },

    /* ------------
     * ACTION MENU
     * ------------ */
    showCommentActionMenu({ commit }, { commentId }): void {
      commit('SHOW_COMMENT_ACTION_MENU', commentId)
    },
    hideCommentActionMenu({ commit }): void {
      commit('HIDE_COMMENT_ACTION_MENU')
    },
  },
}

export default CommentModule
export { CommentState }
